//src/pages/layout/Navbar.jsx
import {
  Box,
  IconButton,
  useTheme,
  InputBase,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useContext, useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from "react-router-dom"; // Import useNavigate
import { ColorModeContext, tokens } from "../../theme";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import AddPatientModal from "../AddPatientModal/index.tsx";
import AddNewDoctor from "../team/Doctors/AddNewDoctor.jsx";
import EditStaff from "../team/Roles/EditRole.jsx";
import SearchDropDown from "../../components/SeacrhDropDown.jsx";
import { getAllPatients } from "../../services/patientService.js";
import { getDoctorById } from "../../services/doctorService.js";
import { getStaffByUserId } from "../../services/StaffService.js";
import { getDoctorByUserId } from "../../services/doctorService.js";
import { getAdmissionByPatientId } from "../../services/PatientAdmissionService.js";
import { getPrescriptionsByPatientId } from "../../services/prescriptionService.js";
import {
  createAttendance,
  getTodaysAttendanceByDoctorId,
  updateAttendance,
  getTodaysAttendanceByStaffId
} from "../../services/attendanceService.js";
import { refreshOrganizationId } from "../../utils/organizationRefresh";
import { useAuth } from "../../context/AuthContext.jsx";
import { format } from "date-fns";
import { logoutWithCheckout } from "../../utils/logoutWithCheckout";
import { performAttendanceCheckOut } from "../../utils/attendanceFlow";
import { isPathModuleStaffRole } from "../../utils/pathRoleUtils";
import Calendar from "../calendar/calendar.jsx";
import AppointmentsTable from "../appointments/AppointmentsTable"; // Import the AppointmentsTable component
import BasicModal from "../../components/BasicModal";
import { getMedicineInventoryList } from "../../services/medicineInventoryService.js";
import NavbarPharmacyLowStockButton from "../../components/NavbarPharmacyLowStockButton.jsx";
import NavbarPathLowStockButton from "../../components/NavbarPathLowStockButton.jsx";
import { canShowPharmacyLowStockAlert } from "../../utils/medicineLowStock";
import { canShowPathLowStockAlert } from "../../utils/pathLowStock";
import {
  STANDARD_FORM_DIALOG_TITLE_SX,
  STANDARD_FORM_DIALOG_ACTIONS_SX,
} from "../../utils/formModalStyles";
import {
  OPD_PATIENTS_REFRESH_EVENT,
  PATH_LAB_DATA_UPDATED_EVENT,
} from "../../utils/opdPatientsRefresh";
import { useSubscriptionCapabilities } from "../../hooks/useSubscriptionCapabilities";

const Navbar = () => {
  const navigate = useNavigate(); // Initialize useNavigate
  const theme = useTheme();
  const colorMode = useContext(ColorModeContext);
  const [open_patient_modal, setOpenPatientModal] = useState(false);
  const [open_calendar_modal, setCalendarModal] = useState(false);
  const [patient_info, setPatientInfo] = useState(null);
  const [patients, setPatients] = useState([]);
  const [doctor, setDoctor] = useState([]);
  const [staff, setStaff] = useState([]);
  const [today_attendance, setTodayAttendance] = useState(null);
  const [marking_attendance, setMarkingAttendance] = useState(false);

  const [showInventoryModal, setShowInventoryModal] = useState(false);
  const [open_doctor_modal, setOpenDoctorModal] = useState(false);
  const [open_staff_modal, setOpenStaffModal] = useState(false);

  const [openAppointmentsModal, setOpenAppointmentsModal] = useState(false); // State for modal visibility
  const [selectedPatient, setSelectedPatient] = useState(null); // State to hold the selected patient
  const [isPatientAdmitted, setIsPatientAdmitted] = useState(false); // State to track if patient is admitted
  const [patientSearchKey, setPatientSearchKey] = useState(0);
  const patientsLastFetchedAtRef = useRef(0);

  const { user, isAuthenticated, logout } = useAuth();
  const {
    isPremiumPackage,
    hasPathPackageAccess,
    loading: subscriptionLoading,
  } = useSubscriptionCapabilities();
  // Attendance UI is Premium-only
  const showAttendanceControls = !subscriptionLoading && isPremiumPackage;
  // Path low-stock alert is Intermediate/Premium only (not Basic)
  const showPathLowStockControls =
    !subscriptionLoading && hasPathPackageAccess;
  // const { isAuthenticated, logout } = useAuth();
  // const user = useSelector((state) => state.user.user); // Access user from Redux
  const dispatch = useDispatch();
  // console.log("Logged in user details form redux use state: ", user);

  const handleLogout = async () => {
    await logoutWithCheckout(logout);
  };

  useEffect(() => {
  const fetchUserDetails = async () => {
    if (user) {
      // Get userId from localStorage (which has the correct userId updated during login)
      // The user object from AuthContext might have the wrong userId if login API returned wrong user
      const storedUser = localStorage.getItem("user");
      let userId = user.id;
      if (storedUser) {
        try {
          const parsedUser = JSON.parse(storedUser);
          if (parsedUser?.id) {
            userId = parsedUser.id;
            console.log(`[Navbar] Using userId ${userId} from localStorage (corrected from login)`);
          }
        } catch (e) {
          console.error("[Navbar] Error parsing stored user, using user.id from context:", e);
        }
      }
      
      // First, ensure organizationId is set (refresh if missing)
      // This is a backup in case App.js refresh didn't complete
      if (storedUser) {
        try {
          const parsedUser = JSON.parse(storedUser);
          if (!parsedUser?.organizationId || parsedUser.organizationId === null || parsedUser.organizationId === "" || Number(parsedUser.organizationId) <= 0) {
            console.log(`[Navbar] organizationId missing, attempting refresh...`);
            const orgId = await refreshOrganizationId();
            if (orgId) {
              console.log(`[Navbar] ✅ organizationId refreshed: ${orgId}`);
              // Don't reload here, let App.js handle it
            }
          }
          
        } catch (e) {
          console.error("[Navbar] Error checking organizationId:", e);
        }
      }
      
      const role = (user.roleName || "").toLowerCase();
      
      if (role === "doctor" || role === "surgeon") {
        try {
          const doctorData = await getDoctorByUserId(userId);
          setDoctor(doctorData);
          if (showAttendanceControls && doctorData?.id) {
            fetchTodaysDoctorAttendance(doctorData.id); // Use the fetched doctor ID
          } else {
            setTodayAttendance(null);
          }
        } catch (error) {
          console.error("Error fetching doctor details:", error);
        }
      } else if (role === "admin" || role === "superadmin") {
        // Admin users - try to fetch staff details, but don't fail if not found
        try {
          const staffData = await getStaffByUserId(userId);
          setStaff(staffData);
          // Admin users don't need attendance tracking, but if staff record exists, we can track it
          if (showAttendanceControls && staffData?.id) {
            fetchTodaysStaffAttendance(staffData.id);
          } else {
            setTodayAttendance(null);
          }
        } catch (error) {
          // Admin user not in staff table - that's okay, continue without staff details
          console.warn("Admin user not found in staff table, continuing without staff details");
          setStaff(null);
        }
      } else {
        // Regular staff users
        try {
          const staffData = await getStaffByUserId(userId);
          setStaff(staffData);
          if (showAttendanceControls && staffData?.id) {
            fetchTodaysStaffAttendance(staffData.id); // Use the fetched staff ID
          } else {
            setTodayAttendance(null);
          }
        } catch (error) {
          console.error("Error fetching staff details:", error);
        }
      }
    } else {
      console.warn("User is not logged in or user data is not available.");
    }
  };

  if (isAuthenticated) {
    fetchUserDetails();
    fetchAllPatients();
  }
  }, [isAuthenticated, user, showAttendanceControls]);

  useEffect(() => {
    const refreshPatients = () => {
      fetchAllPatients();
    };

    // Tab-focus (alt-tab, window switch) fires visibilitychange repeatedly in
    // normal use — only refetch the full org patient list if it's actually
    // been a while, so switching windows doesn't spam /patients. The explicit
    // refresh events above (a patient/lab result actually changed) still
    // always refetch immediately.
    const VISIBILITY_REFRESH_MIN_INTERVAL_MS = 60 * 1000;
    const handleVisibilityChange = () => {
      if (
        document.visibilityState === "visible" &&
        Date.now() - patientsLastFetchedAtRef.current >= VISIBILITY_REFRESH_MIN_INTERVAL_MS
      ) {
        refreshPatients();
      }
    };

    window.addEventListener(PATH_LAB_DATA_UPDATED_EVENT, refreshPatients);
    window.addEventListener(OPD_PATIENTS_REFRESH_EVENT, refreshPatients);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener(PATH_LAB_DATA_UPDATED_EVENT, refreshPatients);
      window.removeEventListener(OPD_PATIENTS_REFRESH_EVENT, refreshPatients);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  const resolvePharmacyPatientType = async (patientId) => {
    try {
      // Same logic as Manage Your Patients → Open Prescription
      const prescriptions = await getPrescriptionsByPatientId(patientId);
      if (prescriptions && Array.isArray(prescriptions) && prescriptions.length > 0) {
        return "opd";
      }

      const admissions = await getAdmissionByPatientId(patientId);
      if (admissions && Array.isArray(admissions) && admissions.length > 0) {
        const hasActiveAdmission = admissions.some((admission) => {
          const status = String(admission.status || "").toLowerCase().trim();
          return status === "admit" || status === "admitted" || status === "admission";
        });
        if (hasActiveAdmission) {
          return "ipd";
        }
      }

      return "opd";
    } catch (error) {
      console.error("Error checking pharmacy patient type:", error);
      return "opd";
    }
  };

  const handlePatientSelect = async (patient) => {
    if (!patient) return;

    await fetchAllPatients();
    setPatientSearchKey((key) => key + 1);
    setPatientInfo(patient); // Set the selected patient info

    // Pharmacy admin: open the same full patient timeline (OPD / IPD)
    if (user && user.roleName === "pharmacy admin") {
      const patientType = await resolvePharmacyPatientType(patient.id);
      navigate(`/manage-patient?patientId=${patient.id}&type=${patientType}`);
      return;
    }

    if (user && (user.roleName === "doctor" || isPathModuleStaffRole(user.roleName))) {
      setCalendarModal(true);
    } else if (patient) {
      setSelectedPatient(patient); // Set the selected patient
      
      // Check if patient is admitted
      try {
        const admissions = await getAdmissionByPatientId(patient.id);
        const hasActiveAdmission = admissions && admissions.length > 0 && 
          admissions.some(admission => 
            admission.status && 
            admission.status.toLowerCase() !== 'discharge' && 
            admission.status.toLowerCase() !== 'discharged'
          );
        
        setIsPatientAdmitted(hasActiveAdmission);
      } catch (error) {
        console.error("Error checking patient admission status:", error);
        setIsPatientAdmitted(false); // Default to false if error occurs
      }
      
      setOpenAppointmentsModal(true); // Open the appointments modal
    }
  };

  const checkInDoctor = async () => {
    setMarkingAttendance(true);
    try {
      if (!doctor || !doctor.id) {
        console.warn("Doctor not available for check-in");
        setMarkingAttendance(false);
        return;
      }
      
      const reqData = {
        doctorId: doctor.id, // Use the fetched doctor ID
        attendanceDate: format(new Date(), "yyyy-MM-dd"),
        status: "PRESENT",
        checkInTime: format(new Date(), "HH:mm"),
        remarks: "present",
      };
      
      // apiClient will automatically add organizationId to the request body
      const data = await createAttendance(reqData);
      setTodayAttendance(data);
    } catch (error) {
      console.error("Error during doctor check-in:", error);
      if (error.response?.status === 404 && error.response?.data?.message?.includes("Doctor not found")) {
        console.error("Backend could not find doctor. This might be because:");
        console.error("1. The backend is not using organizationId to validate the doctor");
        console.error("2. The doctor ID might be incorrect for the organization");
        console.error("Doctor ID:", doctor?.id, "OrganizationId should be:", localStorage.getItem("user") ? JSON.parse(localStorage.getItem("user"))?.organizationId : "not set");
      }
    } finally {
      setMarkingAttendance(false);
    }
  };

  const checkoutDoctor = async () => {
    setMarkingAttendance(true);
    try {
      if (!doctor || !doctor.id) {
        console.warn("Doctor not available for checkout");
        setMarkingAttendance(false);
        return;
      }
      
      // If today_attendance is not available, try to fetch it first
      let attendance = today_attendance;
      if (!attendance || (!attendance.id && !attendance.attendanceId)) {
        try {
          attendance = await getTodaysAttendanceByDoctorId(doctor.id);
          if (attendance) {
            setTodayAttendance(attendance);
          }
        } catch (error) {
          console.error("Error fetching today's attendance:", error);
          setMarkingAttendance(false);
          return;
        }
      }
      
      if (!attendance) {
        console.warn("No attendance record found for checkout");
        setMarkingAttendance(false);
        return;
      }
      
      // Get attendance ID - could be 'id' or 'attendanceId'
      const attendanceId = attendance.id || attendance.attendanceId;
      
      if (!attendanceId) {
        console.error("Attendance ID not found in attendance record:", attendance);
        setMarkingAttendance(false);
        return;
      }
      
      // Exclude id and attendanceId from request body (they're in the URL)
      const { id, attendanceId: _attendanceId, ...attendanceData } = attendance;
      const reqData = {
        ...attendanceData,
        doctorId: doctor.id, // Use the fetched doctor ID
        attendanceDate: format(new Date(), "yyyy-MM-dd"),
        status: "PRESENT",
        checkOutTime: format(new Date(), "HH:mm"),
      };
      const data = await updateAttendance(attendanceId, reqData);
      setTodayAttendance(data);
    } catch (error) {
      console.error("Error during doctor checkout:", error);
    } finally {
      setMarkingAttendance(false);
    }
  };

const checkInStaff = async () => {
  setMarkingAttendance(true);
  try {
    if (!staff || !staff.id) {
      console.warn("Staff not available for check-in");
      setMarkingAttendance(false);
      return;
    }
    
    const reqData = {
      staffId: staff.id, // Use the fetched staff ID
      attendanceDate: format(new Date(), "yyyy-MM-dd"),
      status: "PRESENT",
      checkInTime: format(new Date(), "HH:mm"),
      remarks: "present",
    };
    
    // apiClient will automatically add organizationId to the request body
    const data = await createAttendance(reqData);
    setTodayAttendance(data);
  } catch (error) {
    console.error("Error during staff check-in:", error);
    if (error.response?.status === 404 && error.response?.data?.message?.includes("Staff not found")) {
      console.error("Backend could not find staff. This might be because:");
      console.error("1. The backend is not using organizationId to validate the staff");
      console.error("2. The staff ID might be incorrect for the organization");
      console.error("Staff ID:", staff?.id, "OrganizationId should be:", localStorage.getItem("user") ? JSON.parse(localStorage.getItem("user"))?.organizationId : "not set");
    }
  } finally {
    setMarkingAttendance(false);
  }
};

const checkoutStaff = async () => {
  setMarkingAttendance(true);
  try {
    if (!staff || !staff.id) {
      console.warn("Staff not available for checkout");
      setMarkingAttendance(false);
      return;
    }
    
    // If today_attendance is not available, try to fetch it first
    let attendance = today_attendance;
    if (!attendance || (!attendance.id && !attendance.attendanceId)) {
      try {
        attendance = await getTodaysAttendanceByStaffId(staff.id);
        if (attendance) {
          setTodayAttendance(attendance);
        }
      } catch (error) {
        console.error("Error fetching today's attendance:", error);
        setMarkingAttendance(false);
        return;
      }
    }
    
    if (!attendance) {
      console.warn("No attendance record found for checkout");
      setMarkingAttendance(false);
      return;
    }
    
    // Get attendance ID - could be 'id' or 'attendanceId'
    const attendanceId = attendance.id || attendance.attendanceId;
    
    if (!attendanceId) {
      console.error("Attendance ID not found in attendance record:", attendance);
      setMarkingAttendance(false);
      return;
    }
    
    // Exclude id and attendanceId from request body (they're in the URL)
    const { id, attendanceId: _attendanceId, ...attendanceData } = attendance;
    const reqData = {
      ...attendanceData,
      staffId: staff.id, // Use the fetched staff ID
      attendanceDate: format(new Date(), "yyyy-MM-dd"),
      status: "PRESENT",
      checkOutTime: format(new Date(), "HH:mm"),
    };
    const data = await updateAttendance(attendanceId, reqData);
    setTodayAttendance(data);
  } catch (error) {
    console.error("Error during staff checkout:", error);
  } finally {
    setMarkingAttendance(false);
  }
};

  const fetchTodaysDoctorAttendance = async (id) => {
    try {
      const data = await getTodaysAttendanceByDoctorId(id);
      setTodayAttendance(data);
    } catch (error) {
      if (error?.response?.status === 404) {
        // 404 is expected if no attendance record exists for today
        setTodayAttendance({ status: "NOT_MARKED" });
      } else {
        console.error("Error fetching today's doctor attendance:", error);
        // Set to NOT_MARKED to allow check-in
        setTodayAttendance({ status: "NOT_MARKED" });
      }
    }
  };

  const fetchTodaysStaffAttendance = async (id) => {
    try {
      const data = await getTodaysAttendanceByStaffId(id);
      setTodayAttendance(data);
    } catch (error) {
      if (error?.response?.status === 404) {
        // 404 is expected if no attendance record exists for today
        setTodayAttendance({ status: "NOT_MARKED" });
      } else {
        console.error("Error fetching today's staff attendance:", error);
        // Set to NOT_MARKED to allow check-in
        setTodayAttendance({ status: "NOT_MARKED" });
      }
    }
  };

  const fetchAllPatients = async () => {
    try {
      const data = await getAllPatients();
      setPatients(data);
      patientsLastFetchedAtRef.current = Date.now();
    } catch (error) {
      console.log(error);
    }
  };

  // useEffect(() => {  
  //   const fetchDoctorById = async () => {
  //     try {
  //       const data = await getDoctorById(user.id);
  //       setDoctor(data);
  //       console.log('doctorData', data);
  //     } catch (error) {
  //       console.log(error);
  //     }
  //   };
  //   fetchDoctorById();
  // }, []);

  // useEffect(() => {  
  //   const fetchStaffById = async () => {
  //     try {
  //       const data = await getStaffByUserId(user.id);
  //       setStaff(data);
  //       console.log('staffData', data);
  //     } catch (error) {
  //       console.log(error);
  //     }
  //   };
  //   fetchStaffById();
  // }, []);

  // Check inventory for pharmacy admin and pathology admin
  useEffect(() => {
    const checkInventory = async () => {
      const role = (user?.roleName || "").toLowerCase();
      
      // Only check for pharmacy admin and pathology admin / pathdesk
      if (role === "pharmacy admin" || isPathModuleStaffRole(role)) {
        try {
          const inventoryList = await getMedicineInventoryList();
          // Check if inventory is empty or has no items
          if (!inventoryList || inventoryList.length === 0) {
            setShowInventoryModal(true);
          }
        } catch (error) {
          console.error("Error checking inventory:", error);
          // If there's an error fetching inventory, don't show the modal
          // This prevents showing the modal when there's a network error
        }
      }
    };

    if (user) {
      checkInventory();
    }
  }, [user]);

  const handleCloseInventoryModal = () => {
    setShowInventoryModal(false);
  };

  useEffect(() => {
    const handleBeforeUnload = async () => {
      if (!user) return;
      const storedUser = localStorage.getItem("user");
      let sessionUser = user;
      if (storedUser) {
        try {
          sessionUser = JSON.parse(storedUser);
        } catch (e) {
          // keep context user
        }
      }
      if (sessionUser?.id) {
        await performAttendanceCheckOut({
          roleName: sessionUser.roleName,
          userId: sessionUser.id,
          mobile: sessionUser.mobile,
        });
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [user, staff]);

  // console.log("Todays attendance: ", today_attendance);
  // console.log("user role", user ? user.roleName : "undefined");

  return (
    <Box display="flex" p={2}>
      <Box
        display="flex"
        justifyContent={"space-between"}
        alignItems={"center"}
        width={"100%"}
      >
        <Box display={"flex"} gap={2}>
          {/* {user?.roleName === "doctor" &&
            (today_attendance?.status === "PRESENT" &&
            (today_attendance?.checkOutTime === null ||
              today_attendance?.checkInTime === null) ? (
              today_attendance?.checkOutTime === null ? (
                <Button
                  variant="outlined"
                  onClick={() => checkoutDoctor(user?.id)}
                  disabled={marking_attendance}
                >
                  Check Out
                </Button>
              ) : (
                <Button
                  variant="outlined"
                  onClick={() => checkInDoctor(user?.id)}
                  disabled={marking_attendance}
                >
                  Check In
                </Button>
              )
            ) : today_attendance?.status === "NOT_MARKED" ? (
              <Button
                variant="outlined"
                onClick={() => checkInDoctor(user?.id)}
                disabled={marking_attendance}
              >
                Check In
              </Button>
            ) : (
              <Button variant="text" color="success">
                Attendance Marked
              </Button>
            ))} */}

            {showAttendanceControls && (
              today_attendance?.status === "PRESENT" &&
              (today_attendance?.checkOutTime === null ||
                today_attendance?.checkInTime === null) ? (
              today_attendance?.checkOutTime === null ? (
                <Button
                  variant="outlined"
                  onClick={() => {
                    if (user.roleName === "doctor" || user.roleName === "surgeon") {
                      // checkoutDoctor(user?.id);
                      checkoutDoctor();
                    } else {
                      // checkoutStaff(user?.id); // Use staffId for staff
                      checkoutStaff();
                    }
                  }}
                  disabled={marking_attendance}
                >
                  Check Out
                </Button>
              ) : (
                <Button
                  variant="outlined"
                  onClick={() => {
                    if (user.roleName === "doctor" || user.roleName === "surgeon") {
                      // checkInDoctor(user?.id);
                      checkInDoctor(); // No need to pass user.id
                    } else {
                      // checkInStaff(user?.id); // Use staffId for staff
                      checkInStaff(); // No need to pass user.id
                    }
                  }}
                  disabled={marking_attendance}
                >
                            Check In
                </Button>
              )
            ) : today_attendance?.status === "NOT_MARKED" ? (
              <Button
                variant="outlined"
                onClick={() => {
                  if (user.roleName === "doctor" || user.roleName === "surgeon") {
                    // checkInDoctor(user?.id);
                    checkInDoctor();
                  } else {
                    // checkInStaff(user?.id);
                    checkInStaff();
                  }
                }}
                disabled={marking_attendance}
              >
                Check In
              </Button>
            ) : (
              <Button variant="text" color="success">
                Attendance Marked
              </Button>
            )
            )}

          {user && canShowPharmacyLowStockAlert(user.roleName) && (
            <NavbarPharmacyLowStockButton />
          )}

          {user && showPathLowStockControls && canShowPathLowStockAlert(user.roleName) && (
            <NavbarPathLowStockButton />
          )}

          {user && !isPathModuleStaffRole(user.roleName) && user.roleName !== "pharmacy admin" && (
            <Button
              variant="contained"
              onClick={() => setOpenPatientModal(true)}
            >
              New Patient
            </Button>
          )}
        </Box>
        {/* {doctor?.password === "Doctor@123" && (
          <Typography
            variant="body2"
            color="error"
            sx={{ mb: 1, cursor: 'pointer', textDecoration: 'underline' }}
            //onClick={() => navigate('/update-password')}
          >
            Click here to update your password
          </Typography>
        )} */}

        <Box display={"flex"}>
          {/* SEARCH BAR */}
          {user && (
            <Box display="flex" width={"400px"}>
              <SearchDropDown
                placeholder={
                  user.roleName === "pharmacy admin"
                    ? "Search patients to issue medicine"
                    : "Search existing patients and assign a doctor"
                }
                data={patients}
                onPatientSelect={handlePatientSelect}
                sx={{ mr: 2 }}
              />
            </Box>
          )}

          {/* ICONS */}
          <Box display="flex" justifyContent={"flex-end"}>
            {/* <IconButton onClick={colorMode.toggleColorMode}>
              {theme.palette.mode === "dark" ? (
                <DarkModeOutlinedIcon />
              ) : (
                <LightModeOutlinedIcon />
              )}
            </IconButton>
            <IconButton>
              <NotificationsOutlinedIcon />
            </IconButton>
            <IconButton>
              <SettingsOutlinedIcon />
            </IconButton> */}
            {/* <Button onClick={() => logout()} variant="contained">
              Logout
            </Button> */}

            {/* INVENTORY ALERT MODAL */}
            <Dialog
              open={showInventoryModal}
              onClose={handleCloseInventoryModal}
              PaperProps={{
                sx: {
                  width: 400,
                  borderRadius: 3,
                  p: 2,
                  boxShadow: 5,
                },
              }}
            >
              <DialogTitle sx={STANDARD_FORM_DIALOG_TITLE_SX}>
                Inventory Alert
              </DialogTitle>

              <DialogContent>
                <Box
                  sx={{
                    backgroundColor: "#fff3e0",
                    color: "#e65100",
                    padding: "12px 16px",
                    borderRadius: "8px",
                    fontWeight: 500,
                    textAlign: "center",
                    mb: 2,
                  }}
                >
                  Please add inventory first
                </Box>
              </DialogContent>

              <DialogActions sx={{ ...STANDARD_FORM_DIALOG_ACTIONS_SX, justifyContent: "space-between", px: 3, pb: 2 }}>
                <Button onClick={handleCloseInventoryModal} sx={{ color: "#1976d2" }}>
                  LATER
                </Button>
                <Button
                  variant="contained"
                  onClick={() => {
                    setShowInventoryModal(false);
                    navigate("/inventory");
                  }}
                  sx={{ fontWeight: 600 }}
                >
                  ADD INVENTORY
                </Button>
              </DialogActions>
            </Dialog>
            <Button onClick={handleLogout} variant="contained">Logout</Button>
          </Box>
        </Box>
      </Box>
      <AddPatientModal
        open={open_patient_modal}
        onClose={async () => {
          setOpenPatientModal(false);
          setPatientInfo(null);
          await fetchAllPatients();
          window.dispatchEvent(new CustomEvent(OPD_PATIENTS_REFRESH_EVENT));
        }}
        patient_info={patient_info}
      />

      <AddNewDoctor
        open={open_doctor_modal}
        onClose={async () => {
          setOpenDoctorModal(false);
          setPatientInfo(null);
        }}
        doctor_info={doctor}
      />
      <EditStaff
        open={open_staff_modal}
        onClose={async () => {
          setOpenStaffModal(false);
          setPatientInfo(null);
        }}
        doctor_info={staff}
      />
      <Calendar
        key={`${patient_info?.id || "patient"}-${patientSearchKey}`}
        open={open_calendar_modal}
        onClose={async () => {
          setCalendarModal(false);
          setPatientInfo(null);
          await fetchAllPatients();
        }}
        appointment={{
          patientId: patient_info?.id,
          patientName: patient_info?.name,
          doctorId: doctor.id,         //use the fetched doctor id
          doctorName: user?.username,
        }}
      />

      {/* Appointments Modal */}
      <BasicModal
        open={openAppointmentsModal}
        onClose={() => {
          setOpenAppointmentsModal(false);
          setSelectedPatient(null);
          setPatientInfo(null);
          setIsPatientAdmitted(false);
        }}
        title={selectedPatient?.name ? `Patient: ${selectedPatient.name}` : "Appointments"}
        sx={{ height: "80%", width: "80%", overflow: "auto" }}
      >
        {selectedPatient && (
          <AppointmentsTable
            patient={selectedPatient}
            patients={patients}
            isPatientAdmitted={isPatientAdmitted}
            onClose={() => {
              setOpenAppointmentsModal(false);
              setSelectedPatient(null);
              setPatientInfo(null);
              setIsPatientAdmitted(false);
            }}
          />
        )}
      </BasicModal>
    </Box>
  );
};

export default Navbar;